import { DashboardFolderT, DashboardT } from 'business/definitions';
import {
    filterDashboardsBySearch,
    filterDashboardsFoldersBySearch,
} from '../helpers/filterDashboardsList';
import { useEffect, useMemo, useState } from 'react';

import { updateDashboardFolders } from '../helpers/updateDashboardFoldersList';
import { useIsAdminOrSupportUser } from 'hooks/user';

export const useDashboardSearchAndFolders = (
    dashboardFolders: DashboardFolderT[],
    dashboards: DashboardT[],
) => {
    const isAdminOrSupport = useIsAdminOrSupportUser();
    const [searchValue, setSearchValue] = useState('');
    const [currentFolder, setCurrentFolder] = useState<DashboardFolderT | null>(
        null,
    );

    const isSearching = searchValue !== '';

    const displayedDashboards = useMemo(() => {
        if (currentFolder) {
            return filterDashboardsBySearch(
                currentFolder.dashboards,
                searchValue,
            );
        }
        return filterDashboardsBySearch(dashboards, searchValue, true);
    }, [currentFolder, dashboards, searchValue]);

    const displayedDashboardFolders = useMemo(
        () =>
            updateDashboardFolders(
                filterDashboardsFoldersBySearch(dashboardFolders, searchValue),
                dashboards,
                isAdminOrSupport,
            ),
        [dashboardFolders, dashboards, searchValue, isAdminOrSupport],
    );

    useEffect(() => {
        if (!isSearching && currentFolder) {
            const matchingFolder = displayedDashboardFolders.find(
                (folder) => folder.id === currentFolder.id,
            );
            setCurrentFolder(matchingFolder ?? null);
        }
    }, [displayedDashboardFolders, currentFolder, isSearching]);

    const handleFolder = (folder: DashboardFolderT | null) => {
        setCurrentFolder(folder);
        setSearchValue('');
    };

    return {
        searchValue,
        setSearchValue,
        isSearching,
        currentFolder,
        setCurrentFolder,
        displayedDashboards,
        displayedDashboardFolders,
        handleFolder,
    };
};
